import type { FunnelPageRelationship, FunnelPageRelationshipKind } from './funnelRelationships';

// Page 1 = Entry Page, page 2 = Subsequent Offer, page 3+ = Following Offer.
export function getFunnelPageRole(index: number): string {
    if (index === 0) return 'Entry Page';
    if (index === 1) return 'Subsequent Offer';
    return 'Following Offer';
}

/**
 * Role label used in the sidebar / dashboard row headers. Branching relationships
 * (`upsell` / `downsell`) take precedence over the positional role so a
 * purchased-path page reads as "Upsell Page" instead of "Following Offer".
 */
export function getFunnelPageRoleLabel(stepNumber: number, relationship: FunnelPageRelationship | undefined): string {
    if (relationship?.kind === 'upsell') return 'Upsell Page';
    if (relationship?.kind === 'downsell') return 'Downsell Page';
    return getFunnelPageRole(stepNumber - 1);
}

export interface FunnelPageRoleDetails {
    title: string;
    description: string;
    productSelectorDescription: string;
}

const ROLE_DETAILS: Record<FunnelPageRelationshipKind, FunnelPageRoleDetails> = {
    lead_capture: {
        title: 'Entry Page',
        description:
            'Capture visitors by offering something valuable up front — a freebie, discount, guide, or any product from your store.',
        productSelectorDescription: 'Select a Lead Magnet or any product from your store.',
    },
    main_offer: {
        title: 'Subsequent Offer',
        description: 'Present your main product or service with a compelling pitch.',
        productSelectorDescription: 'Select a paid Landing Page or any product from your store.',
    },
    upsell: {
        title: 'Upsell Page',
        description: 'Offer an upgrade or complementary product after the customer purchases.',
        productSelectorDescription: 'Select a product from your store to offer as an upsell.',
    },
    downsell: {
        title: 'Downsell Page',
        description: 'Win back customers who skipped your main offer with a different angle.',
        productSelectorDescription: 'Select a product from your store to offer as a downsell.',
    },
    follow_up: {
        title: 'Following Offer',
        description: 'Offer a complementary product to boost order value after the subsequent offer.',
        productSelectorDescription: 'Select a product from your store to offer as a following step.',
    },
};

/**
 * Role-specific copy for each funnel step. Drives the editor header, the
 * product-selector helper text, and any empty-state messaging.
 *
 * When a relationship is provided it is the source of truth (this correctly
 * accounts for hidden pages so the first *visible* step reads as the entry
 * page even when an order-1 page is hidden). Falls back to positional copy
 * when no relationship is available — used by suggested / draft rows where
 * the page hasn't been classified yet.
 */
export function getFunnelPageRoleDetails(
    index: number,
    relationship?: FunnelPageRelationship | undefined
): FunnelPageRoleDetails {
    if (relationship) return ROLE_DETAILS[relationship.kind];
    if (index === 0) return ROLE_DETAILS.lead_capture;
    if (index === 1) return ROLE_DETAILS.main_offer;
    return ROLE_DETAILS.follow_up;
}
